// =============================================================================
// ATS REGISTRY — the W1-11 shared, cross-user poll target list.
// (docs/briefs/launch-week-briefs.md §A W1-11; follows W1-10's atsAutoDiscovery
// resolver, which is the ONLY writer of new rows here.)
//
// SHARED, NO userId — same shape-of-law as salaryIntel (jobs.ts) and
// publicEvents (public_events.ts): "greenhouse.io/anthropic is a public board
// token" is one truth for every user watching it, not a per-user fact. THE
// PRIVACY LINE (W1-11d): this table carries ZERO user linkage — who watches
// what lives ONLY in watchlistSources.registryId (per-user rows). A read of
// this table alone can never answer "which users watch X" — there is no
// column that could answer it.
//
// DEDUP: unique (ats_kind, token) — one registry row per distinct board,
// regardless of how many users' auto-discovery deposits it or how many
// subscribe. `token` is the boardSlug/tenant-site key atsAutoDiscovery.ts
// already extracts (AtsTokenMatch.boardSlug) — reused verbatim, not a new
// vocabulary.
//
// AMORTIZED POLLING (W1-11c): the cron polls each registry row ONCE per
// cycle regardless of subscriber count; lib/registryPoll.ts fans the single
// poll result out to every subscribing user's own ingest/score/bucket path.
//
// FRESHNESS AS FIRST-CLASS DATA (W1-11e, his call: "each of the fetches with
// how stale each is"): lastPolledAt (every attempt, success or failure) +
// lastChangedAt (only when the poll's candidate set actually differed from
// the previous poll — the "did anything really change" signal, distinct from
// "did we merely check"). Both timestamps come from the temporal clock
// (c.get('clock').now in routes; the cron's own resolved Date in
// lib/registryPoll.ts), never a bare `new Date()` read deep in a detector —
// same discipline TemporalService.ts documents. Never fabricated: a registry
// row that has never been polled carries lastPolledAt: null, read as
// "never polled," never coerced to a fake recent time.
// =============================================================================

import { index, integer, pgTable, text, timestamp, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

/** The ATS kinds this registry tracks. Mirrors atsAutoDiscovery.ts's AtsKind
 *  (greenhouse | lever | ashby | smartrecruiters | workday) — kept as a plain
 *  string column (not a DB enum) so a future ATS kind is a data value, not a
 *  migration, matching watchlistSources.kind's existing convention. */
export const atsRegistry = pgTable(
  'ats_registry',
  {
    id: id(),
    // Display name as last seen from an ingest — best-effort, for the UI list.
    // NOT the dedup key (token is) — two spellings of the same company must
    // still land on ONE registry row via the same token.
    company: varchar('company', { length: 200 }).notNull(),
    // greenhouse | lever | ashby | smartrecruiters (atsAutoDiscovery.ts's AtsKind).
    atsKind: varchar('ats_kind', { length: 24 }).notNull(),
    // The board/org/tenant-site slug — atsAutoDiscovery.ts's AtsTokenMatch.boardSlug.
    token: varchar('token', { length: 200 }).notNull(),
    // The company's own careers page the token was resolved from (audit only,
    // mirrors AtsTokenMatch.sourceUrl — never re-fetched as a poll target).
    careersUrl: text('careers_url'),
    // W1-10b — Workday ONLY: the "3" in wd3.myworkdayjobs.com. NOT derivable
    // from tenant+site alone (a live probe during this build confirmed the
    // bare-tenant host doesn't even resolve in DNS for a tenant that IS
    // wd{N}-hosted) — without this column, lib/registryPoll.ts's amortized
    // poller has no way to reconstruct a working Workday CXS URL from the
    // registry row alone, so a wd{N}-hosted board (the norm for large
    // enterprises per this build's own probes) would silently fail every
    // amortized-poll cycle forever. Null for every non-workday row AND for a
    // genuinely bare-tenant Workday board (an honest null, never invented).
    wdNum: varchar('wd_num', { length: 8 }),
    // When this registry row was first created (distinct from the inherited
    // `createdAt` timestamp only in NAME — kept as its own column because the
    // brief names it explicitly as part of the row's public shape: "{company,
    // atsKind, token, careersUrl, discoveredAt, discoveredVia}").
    discoveredAt: timestamp('discovered_at', { withTimezone: true }).notNull(),
    // Free-text provenance: which user/flow's auto-discovery deposited it
    // (e.g. 'watchlist.poll', 'jobs.ingest', 'watchlist.discover-manual') —
    // audit only, never a join key back to a user (the privacy line).
    discoveredVia: varchar('discovered_via', { length: 64 }),
    // ── W1-11(e) freshness — first-class, served on every read ────────────
    // Every poll ATTEMPT (success or failure) stamps this — "how long ago did
    // we last check." Null = never polled since this row was created.
    lastPolledAt: timestamp('last_polled_at', { withTimezone: true }),
    // Stamped ONLY when a poll's candidate set differs from the prior poll's
    // (a real change, not just a re-check) — "how long ago did this actually
    // move." Null = never observed a change (including: never polled).
    lastChangedAt: timestamp('last_changed_at', { withTimezone: true }),
    // Count of postings the most recent successful poll returned — cheap
    // freshness-adjacent signal for the UI list, honest null before any poll.
    lastPolledCount: integer('last_polled_count'),
    lastError: text('last_error'),
    ...timestamps,
  },
  (t) => ({
    // The dedup guard: one row per (atsKind, token). Applied via the SQL file
    // as a UNIQUE constraint (Drizzle's `.unique()` on a composite pair needs
    // a table-level unique, added there rather than guessed here) — this
    // index exists for the STALEST-FIRST poll-ordering read pattern.
    kindIdx: index('ats_registry_kind_idx').on(t.atsKind),
    polledIdx: index('ats_registry_last_polled_idx').on(t.lastPolledAt),
  }),
);
